/**
 * quizBankRules — what the Quiz Center and the question bank decide, pure.
 *
 * The setup steps (what is done, what comes next), the ways to add questions,
 * who may use the AI, and how the bank's table becomes a request. The pages
 * render these; `__tests__/components/QuizBank/quizBankRules.test.js` pins them.
 */

import { DIFFICULTIES, QUESTION_TYPES } from "./questionEditor/questionRules";

/** The editor's own names for types and difficulties — one vocabulary. */
export const QUESTION_TYPE_LABELS = Object.fromEntries(QUESTION_TYPES.map((t) => [t.value, t.label]));
export const DIFFICULTY_LABELS = Object.fromEntries(DIFFICULTIES.map((d) => [d.value, d.label]));

/** AI drafting: the platform library and an institute's own admins — never a
 *  trainer. An institute's drafts draw on its AI token pool. */
export const canUseQuizAi = (usertype) => ["tenantadmin", "superadmin", "admin"].includes(usertype);

/** The signed-in user's type, or "" — read once by the pages that offer AI. */
export function currentUsertype() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}")?.usertype || "";
  } catch {
    return "";
  }
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Top-level categories and their subtopics, from the flat category list. */
export function splitCategories(flat = []) {
  const top = flat.filter((c) => !c.parentCategory);
  const subs = flat.filter((c) => c.parentCategory);
  return { top, subs };
}

export const subtopicsOf = (flat = [], categoryId) =>
  flat.filter((c) => c.parentCategory && String(c.parentCategory) === String(categoryId));

/**
 * The Quiz Center's four setup steps, each done from a live count. `current`
 * is the first one not done — the step the page puts its actions on — or -1
 * when everything is in place.
 */
export function setupSteps({ categories = 0, questions = 0, assessments = 0, published = 0 } = {}) {
  const steps = [
    {
      key: "topics",
      title: "Organise your topics",
      text: "Make a category for each subject or round — Aptitude, English, Python, ICD-10 — and subtopics inside it.",
      done: categories > 0,
      status: categories > 0 ? plural(categories, "category", "categories") : "",
    },
    {
      key: "questions",
      title: "Add questions",
      text: "One at a time with a guided form, from a spreadsheet, or drafted by AI for a subtopic.",
      done: questions > 0,
      status: questions > 0 ? `${plural(questions, "question")} in the bank` : "",
    },
    {
      key: "assessment",
      title: "Build an assessment",
      text: "Pick a format — a quick quiz, a class test, a placement test — and the builder sets up the sections, timer and rules.",
      done: assessments > 0,
      status: assessments > 0 ? plural(assessments, "assessment") : "",
    },
    {
      key: "publish",
      title: "Give it to students",
      text: "Publish it to a batch, a course or chosen students. They are told, and their results come back here.",
      done: published > 0,
      status: published > 0 ? `${published} published` : "",
    },
  ];
  const current = steps.findIndex((s) => !s.done);
  return { steps, current, doneCount: steps.filter((s) => s.done).length };
}

/**
 * The question bank's own three steps, for a bank with no questions yet:
 * category → subtopic → questions.
 */
export function bankSteps({ categories = 0, subtopics = 0, questions = 0 } = {}) {
  const steps = [
    {
      key: "category",
      title: "Make a category",
      text: "A subject or a round of a test — Aptitude, English, Python, Medical terminology.",
      done: categories > 0,
    },
    {
      key: "subtopic",
      title: "Add subtopics inside it",
      text: "Smaller topics — Percentages, Time and work, Reading comprehension. Random draws and AI work topic by topic.",
      done: subtopics > 0,
    },
    {
      key: "questions",
      title: "Add questions",
      text: "One at a time, from a spreadsheet, or drafted by AI and checked by you.",
      done: questions > 0,
    },
  ];
  return { steps, current: steps.findIndex((s) => !s.done) };
}

/** The ways to add questions, in the order the chooser offers them. */
export const ADD_METHODS = [
  {
    key: "single",
    title: "One at a time",
    text: "A guided form: topic, type, the question, its answer, marks — then check and save.",
  },
  {
    key: "upload",
    title: "Upload a spreadsheet",
    text: "CSV, Excel or JSON. Match your columns, check the rows, upload them all at once.",
  },
  {
    key: "ai",
    title: "Draft with AI",
    text: "Pick a subtopic and how many. AI drafts them; you keep the good ones.",
    ai: true,
  },
  {
    key: "passage",
    title: "A passage or case",
    text: "One paragraph, chart or PDF with several questions about it — reading comprehension, a medical coding case.",
  },
];

export const addMethodsFor = ({ ai = false } = {}) => ADD_METHODS.filter((m) => !m.ai || ai);

// ── The bank's table ────────────────────────────────────────────────────

export const DEFAULT_SORT = { key: "createdAt", direction: "desc" };

/** A header sort as the list endpoint's `sort`. */
export function sortParam(sort) {
  const s = sort || DEFAULT_SORT;
  if (s.key === "attempts") return s.direction === "asc" ? "least-attempted" : "most-attempted";
  return s.direction === "asc" ? "oldest" : "newest";
}

/** Column filter values (arrays from the header funnels) as list parameters —
 *  several values travel comma-separated and mean "any of". */
const joined = (v) => (Array.isArray(v) ? v.filter(Boolean).join(",") : String(v || ""));

export function listParams({ page = 1, pageSize = 20, search = "", sort, columnFilters = {} } = {}) {
  const params = { page, limit: pageSize, sort: sortParam(sort) };
  const q = String(search || "").trim();
  if (q) params.search = q;
  const category = joined(columnFilters.category);
  const questionType = joined(columnFilters.questionType);
  const difficulty = joined(columnFilters.difficulty);
  if (category) params.category = category;
  if (questionType) params.questionType = questionType;
  if (difficulty) params.difficulty = difficulty;
  return params;
}

/** "7 of 9 right (78%)" — or "Not attempted yet". */
export function attemptsLine(q) {
  const total = Number(q?.totalAttempts) || 0;
  if (!total) return "";
  const right = Number(q?.correctAttempts) || 0;
  return `${right} of ${total} right (${Math.round((right / total) * 100)}%)`;
}

export const questionPath = (q) =>
  [q?.category?.name, q?.subcategory?.name].filter(Boolean).join(" › ");

/** The folders a "Find a category" search keeps: a match on the name, the
 *  description, or — for a category — one of its subtopics' names. */
export function findFolders(folders = [], query = "") {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return folders;
  const hit = (v) => String(v || "").toLowerCase().includes(q);
  return folders.filter((f) => hit(f.name) || hit(f.description) || (f.subfolders || []).some((s) => hit(s.name)));
}

/** A folder's price on the marketplace, as a badge reads it. */
export function priceLabel(pricing) {
  if (!pricing || pricing.model !== "paid") return "Free";
  const sym = pricing.currency === "INR" ? "₹" : `${pricing.currency || ""} `;
  const per = pricing.period === "monthly" ? "/mo" : pricing.period === "yearly" ? "/yr"
    : pricing.period === "lifetime" ? " lifetime" : "";
  return `${sym}${pricing.amount || 0}${per}`;
}

export { plural };
